import type { AxiosInstance, AxiosResponse } from 'axios';

import type { Client } from '@/models/client';
import type { Media, MediaFile } from '@/models/media';
import type { RootResponse } from '@/models/rootResponse';

export type GetClientFooterImage = {
   clientId: string;
};

export type AddDocument = {
   name: string;
   file: string | File;
   creatorId: string;
};

export type UpdateDocument = {
   name: string;
   file: string | File;
   userId: string;
   fileId?: number;
};

class ClientService {
   private provider: AxiosInstance;

   constructor(provider: AxiosInstance) {
      this.provider = provider;
   }

   async getClientDocumentation(
      client_uuid: string,
   ): Promise<RootResponse<Media[]>> {
      const response: AxiosResponse<RootResponse<Media[]>> =
         await this.provider.get(
            `/clients/${client_uuid}/documentations?app=survey`,
         );
      return response.data;
   }

   async getCurrentClient(clientId: string): Promise<RootResponse<Client>> {
      const response: AxiosResponse<RootResponse<Client>> =
         await this.provider.get(`/clients/${clientId}`);
      return response.data;
   }

   async downloadMedia(mediaId: string): Promise<ArrayBuffer> {
      const response: AxiosResponse<ArrayBuffer> = await this.provider.get(
         `/media/${mediaId}/download`,
         {
            responseType: 'arraybuffer',
         },
      );
      return response.data;
   }

   async updateClient({
      client,
      clientId,
   }: {
      client: Partial<Client>;
      clientId: string;
   }): Promise<RootResponse<Client>> {
      const response: AxiosResponse<RootResponse<Client>> =
         await this.provider.put(`/clients/${clientId}`, client);
      return response.data;
   }

   async updateClientFooter({
      clientId,
      body,
   }: {
      clientId: string;
      body: FormData;
   }): Promise<RootResponse<Client>> {
      try {
         const response: AxiosResponse<RootResponse<Client>> =
            await this.provider.post(`/clients/${clientId}`, body, {
               headers: {
                  'Content-Type': 'multipart/form-data',
               },
            });
         return response.data;
      } catch (err) {
         return Promise.reject(err);
      }
   }

   async getClientFooterImage({
      clientId,
   }: GetClientFooterImage): Promise<RootResponse<Media>> {
      try {
         const response: AxiosResponse<RootResponse<Media>> =
            await this.provider.get(`/clients/${clientId}/footer`);
         return response.data;
      } catch (err) {
         return Promise.reject(err);
      }
   }

   async deleteMedia(mediaId: string): Promise<void> {
      try {
         const response = await this.provider.delete(`/media/${mediaId}`);
         return response.data;
      } catch (error) {
         return Promise.reject(error);
      }
   }

   async getDocuments(): Promise<RootResponse<MediaFile[]>> {
      try {
         const response: AxiosResponse<RootResponse<MediaFile[]>> =
            await this.provider.post(`/support-documents/search`, {
               filters: [
                  { field: 'application_name', operator: '=', value: 'survey' },
               ],
            });
         return response.data;
      } catch (err) {
         return Promise.reject(err);
      }
   }

   async addDocument({ name, file, creatorId }: AddDocument) {
      const formData = new FormData();

      formData.append('name', name);
      formData.append('creator_id', creatorId);
      formData.append('application_name', 'survey');
      formData.append('media[0][file]', file);
      formData.append('media[0][collection]', 'support_document');
      try {
         return await this.provider.post(`/support-documents`, formData, {
            headers: {
               'Content-Type': 'multipart/form-data',
            },
         });
      } catch (err) {
         return Promise.reject(err);
      }
   }

   async deleteDocument(mediaId: string): Promise<void> {
      try {
         const response = await this.provider.delete(
            `/support-documents/${mediaId}`,
         );
         return response.data;
      } catch (error) {
         return Promise.reject(error);
      }
   }

   async updateDocument({ name, file, userId, fileId }: UpdateDocument) {
      const formData = new FormData();
      const hasFile = typeof file === 'object' && file && 'path' in file;

      formData.append('_method', 'PUT');
      formData.append('name', name);
      formData.append('creator_id', userId);
      formData.append('application_name', 'survey');

      if (hasFile) {
         formData.append('media[0][file]', file);
         formData.append('media[0][collection]', 'support_document');
      }

      try {
         return await this.provider.post(
            `/support-documents/${fileId}`,
            formData,
            {
               headers: {
                  'Content-Type': 'multipart/form-data',
               },
            },
         );
      } catch (error) {
         return Promise.reject(error);
      }
   }
}

export default ClientService;
